import React, { useState, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { validateCart } from "./lib/cartApis";
import { reinitializeSession } from "./lib/sessionInit"; // Import session initialization
import { preloadPlaceholderImage } from "./utils/imageUtils"; // Import preloadPlaceholderImage
import useClarity, { trackEvent } from "./hooks/useClarity"; // Update import
import useGTM, { trackEvent as trackGTMEvent } from "./hooks/useGTM"; // Import GTM hook
import apiCache from "./lib/apiCache"; // Import apiCache
// import cacheInvalidator from "./lib/cacheInvalidator"; // Import cacheInvalidator - removed for now
import Home from "./views/Home/Home";
import MiniCart from "./components/MiniCart/MiniCart";
import Product from "./views/Product/Product";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import CategoryPage from "./views/Category/CategoryPage";
import Cart from "./views/Cart/Cart";
import { LocationProvider } from "./context/LocationContext"; // Location Context
import { CheckoutProvider } from "./context/CheckoutContext"; // Checkout Context
import Checkout from "./views/Checkout/Checkout";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { setCart, clearCart } from "./redux/cartReducer"; // Add clearCart import
import { processCartResponse } from "./utils/cartResponseUtils";
import OrderComplete from "./views/OrderComplete/OrderComplete";
import OrderLookup from "./views/OrderLookup/OrderLookup";
import NotFound from "./views/NotFound/NotFound";
import SearchResults from "./views/SearchResults/SearchResults";
import Blog from "./views/Blog/Blog";
import BlogArticle from "./views/BlogArticle/BlogArticle";
import BlogSearchResults from "./views/BlogSearchResults/BlogSearchResults";
import Faq from "./views/FAQ/Faq";
import Legales from "./views/Legales/Legales";
import FormRenderer from "./views/Forms/Forms";
import FormsSuccess from "./views/Forms/FormsSuccess/FormsSuccess";
import AboutUs from "./views/AboutUs/AboutUs";
import PartnersPage from "./views/PartnersPage/PartnersPage";
import WhatsAppButton from "./components/WhatsAppButton/WhatsAppButton";
import { Provider } from "react-redux";
import store from "./redux/store";
import CartOperationEventHandler from "./services/CartOperationEventHandler"; // Import CartOperationEventHandler
import GlobalCartSyncService from "./services/GlobalCartSyncService"; // Import GlobalCartSyncService
import CartAutoFlushService from "./services/CartAutoFlushService"; // Import CartAutoFlushService
import { HelmetProvider } from "react-helmet-async"; // Import HelmetProvider

const Layout: React.FC = () => {
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);

  const toggleMiniCart = () => {
    setIsMiniCartOpen((prev) => !prev);
  };

  return (
    <div className="app">
      <LocationSyncWrapper />
      <Header toggleMiniCart={toggleMiniCart} />
      <Outlet context={{ toggleMiniCart }} />
      <Footer />
      {isMiniCartOpen && <MiniCart toggleSidenav={toggleMiniCart} />}
      <WhatsAppButton />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/search", // This route will automatically handle query parameters
        element: <SearchResults />,
      },
      {
        path: "/c/:categoryUrl",
        element: <CategoryPage />,
      },
      {
        path: "/p/:productUrl",
        element: <Product />,
      },
      {
        path: "/cart",
        element: <Cart />,
      },
      {
        path: "/checkout",
        element: (
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        ),
      },
      {
        path: "/order-success",
        element: <OrderComplete />, // <-- Not wrapped in ProtectedRoute
      },
      {
        path: "/order-lookup",
        element: <OrderLookup />,
      },
      {
        path: "/formulario/:formUrl",
        element: <FormRenderer />,
      },
      {
        path: "/formulario/exito/:formId/:submissionId",
        element: <FormsSuccess />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
      {
        path: "/blog",
        element: <Blog />,
      },
      {
        path: "/blog/:blogUrl",
        element: <BlogArticle />,
      },

      {
        path: "/nosotros",
        element: <AboutUs />,
      },
      {
        path: "/preguntas-frecuentes",
        element: <Faq />,
      },
      {
        path: "/se-parte-de-evefant",
        element: <PartnersPage />,
      },
      {
        path: "/legales",
        element: <Legales />,
      },
      {
        path: "/legales/:articleUrl", // Make sure this parameter name matches useParams in Legales component
        element: <Legales />,
      },
      {
        path: "/blog/search",
        element: <BlogSearchResults />,
      },
    ],
  },
]);

const LocationSyncWrapper = () => {
  const location = useLocation();

  // Initialize Clarity with your project ID and options
  const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID || "";

  // Initialize GTM with your container ID
  const GTM_ID = process.env.REACT_APP_GTM_ID || "";

  // Example of using enhanced Clarity features - customize as needed
  const clarityOptions = {
    // User authentication is not currently used

    // Add relevant tags for your business
    tags: {
      environment: process.env.NODE_ENV || "development",
      platform: "web",
      version: "1.0.0", // Your app version
      currentPath: location.pathname, // Track current path as a tag
    },

    // Optionally track page type as a custom event
    events: ["page_view"],
  };

  // GTM options
  const gtmOptions = {
    debug: process.env.NODE_ENV !== "production",
    userProperties: {
      environment: process.env.NODE_ENV || "development",
      platform: "web",
      version: "1.0.0",
    },
  };

  useClarity(CLARITY_PROJECT_ID, clarityOptions);
  useGTM(GTM_ID, gtmOptions);

  // Track page view when location changes
  useEffect(() => {
    // Extract page type from path
    const pageType =
      location.pathname === "/"
        ? "home"
        : location.pathname.split("/")[1] || "unknown";

    // Track page view event with additional data in Clarity
    trackEvent("page_view", {
      path: location.pathname,
      pageType,
      search: location.search,
      referrer: document.referrer,
    });

    // Also track in GTM
    trackGTMEvent("page_view", {
      page_path: location.pathname,
      page_title: document.title,
      page_type: pageType,
      page_search: location.search,
      page_referrer: document.referrer,
    });
  }, [location.pathname, location.search]);

  return null; // This component doesn't render anything
};

function App() {
  const dispatch = useDispatch();

  // Initialize session and cart validation on application startup
  useEffect(() => {
    // Ensure session is initialized
    reinitializeSession();

    // Preload placeholder image when the app starts
    preloadPlaceholderImage();

    // Initialize the cache invalidator - commented out for now
    // cacheInvalidator.initialize();
    // console.log("Cache invalidation system initialized");

    // Expose cache control for debugging purposes
    if (process.env.NODE_ENV !== "production") {
      (window as any).clearApiCache = apiCache.clearAll.bind(apiCache);
      (window as any).getCacheStatus = apiCache.getStatus.bind(apiCache);
      // (window as any).invalidateCache = cacheInvalidator.invalidate.bind(cacheInvalidator);
      console.log(
        "API cache control available via window.clearApiCache() and window.getCacheStatus()"
      );
    }

    async function initializeCart() {
      try {
        // Check if we have already validated the cart in this browser session
        const isCartValidated =
          sessionStorage.getItem("cartValidated") === "true";

        console.log(
          "%c🔍 [SESSION START] Cart validation check initiated",
          "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
        );
        console.log(
          `%c🗂️ [SESSION START] Cart already validated in this session? ${
            isCartValidated ? "YES" : "NO"
          }`,
          "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
        );

        if (isCartValidated) {
          console.log(
            "%c✅ [SESSION START] Skipping cart validation (already done in this session)",
            "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
          );
          return; // Skip validation if already done in this browser session
        }

        // Get cartId from localStorage
        const cartId = localStorage.getItem("cartId");

        if (cartId) {
          console.log(
            `%c🛒 [SESSION START] Found cartId in localStorage: ${cartId.substring(
              0,
              6
            )}...`,
            "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
          );
          console.log(
            "%c📤 [SESSION START] Sending cart validation request to server...",
            "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
          );

          // Call the validateCart function directly instead of through dispatch
          const response = await validateCart(cartId);

          console.log(
            `%c📥 [SESSION START] Cart validation response received: ${
              response?.isValid ? "VALID" : "INVALID"
            }`,
            `background: ${
              response?.isValid ? "#006400" : "#8b0000"
            }; color: #fff; padding: 4px; border-radius: 4px;`
          );

          if (response?.isValid) {
            // Cart is valid, mark it as validated for this browser session
            console.log(
              "%c✅ [SESSION START] Cart validated successfully, marking as validated in sessionStorage",
              "background: #006400; color: #fff; padding: 4px; border-radius: 4px;"
            );
            sessionStorage.setItem("cartValidated", "true");

            // Update redux with cart data if needed
            if (response.cartId) {
              dispatch(setCart(processCartResponse(response)));
            }
          } else {
            // Cart is invalid, remove it from localStorage
            console.log(
              "%c❌ [SESSION START] Cart validation failed, removing cartId from localStorage",
              "background: #8b0000; color: #fff; padding: 4px; border-radius: 4px;"
            );
            localStorage.removeItem("cartId");

            // Clear the Redux cart state to match localStorage
            console.log(
              "%c🧹 [SESSION START] Clearing Redux cart state",
              "background: #8b0000; color: #fff; padding: 4px; border-radius: 4px;"
            );
            dispatch(clearCart());
          }
        } else {
          console.log(
            "%c📭 [SESSION START] No cartId found in localStorage, will create one when user adds items",
            "background: #4b0082; color: #fff; padding: 4px; border-radius: 4px;"
          );
        }
      } catch (error) {
        console.error(
          "%c❌ [SESSION START] Error validating cart:",
          "background: #8b0000; color: #fff; padding: 4px; border-radius: 4px;",
          error
        );
      }
    }

    // Initialize cart
    initializeCart();

    // Log session state for debugging
    console.log("🔍 App component mounted - Session should be initialized");

    // Initialize the cart operation system on app start
    CartOperationEventHandler.initialize();
    console.log("Cart operation system initialized");

    // Initialize the global cart sync service with the store
    GlobalCartSyncService.initialize(store);
    console.log("Global cart sync service initialized");

    // Initialize auto-flush service for cart operations
    CartAutoFlushService.getInstance().initialize();
    console.log("Cart auto-flush service initialized");

    return () => {
      // Clean up event listeners when the app unmounts
      // cacheInvalidator.cleanup();
      CartAutoFlushService.getInstance().cleanup();
    };
  }, [dispatch]);

  return (
    <Provider store={store}>
      <LocationProvider>
        <CheckoutProvider>
          <HelmetProvider>
            {" "}
            {/* Wrap your application with HelmetProvider */}
            <div className="App">
              <RouterProvider router={router} />
            </div>
          </HelmetProvider>
        </CheckoutProvider>
      </LocationProvider>
    </Provider>
  );
}

export default App;
